import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { Check } from "lucide-react";
import * as React from "react";
import { useDialogGuard } from "@/hooks/use-dialog-guard";
import { cn } from "@/lib/utils";
import { optionHoverClass } from "./option-row";

/**
 * Design 14h — 16px box, 4px radius, primary fill when checked. The
 * indeterminate state is an 8×2 dash; it belongs to select-all rows, whose
 * children drive it.
 *
 * The glyph is 10px inside a 16px box, so it needs a heavier stroke than the
 * icon set's default to stay legible at that size.
 */
const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, onCheckedChange, ...props }, ref) => {
  const { markDirty } = useDialogGuard();

  return (
    <CheckboxPrimitive.Root
      ref={ref}
      onCheckedChange={(checked) => {
        markDirty();
        onCheckedChange?.(checked);
      }}
      className={cn(
        optionHoverClass,
        "group grid h-4 w-4 shrink-0 place-content-center peer rounded-sm border border-primary shadow-sm transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground",
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="grid place-content-center text-current">
        <Check className="h-2.5 w-2.5 group-data-[state=indeterminate]:hidden" strokeWidth={3} />
        <span className="hidden h-0.5 w-2 rounded-full bg-current group-data-[state=indeterminate]:block" />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
